import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppLayout } from '../layouts/AppLayout';
import { Card, Badge, Button } from '../components/ui';
import { useTemplates } from '../hooks/useTemplates';
import { Clock, Layers, Video, BookOpen } from 'lucide-react';

const FORMAT_LABELS: Record<string, string> = {
  shorts_featured_video: 'Shorts Featured Video',
  full_screen_interstitial: 'Full Screen Interstitial',
};

function TemplatesPage() {
  const navigate = useNavigate();
  const { templates, loading, error } = useTemplates();
  const [videoErrors, setVideoErrors] = useState<Set<string>>(new Set());

  const handleVideoError = (id: string) => {
    setVideoErrors((prev) => new Set(prev).add(id));
  };

  return (
    <AppLayout
      title="Template Library"
      subtitle="Browse available templates and start a new job from any template."
    >
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header actions */}
        <div className="flex items-center justify-between">
          <p className="text-sm text-text-secondary">
            {loading ? 'Loading templates…' : `${templates.length} active template${templates.length === 1 ? '' : 's'}`}
          </p>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => navigate('/template-guide')}
            className="flex items-center gap-2"
          >
            <BookOpen className="w-4 h-4" />
            Template Guide
          </Button>
        </div>

        {/* Error state */}
        {error && (
          <Card>
            <p className="text-sm text-accent-red text-center py-4">
              Failed to load templates: {error}
            </p>
          </Card>
        )}

        {/* Loading skeleton */}
        {loading && !error && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((n) => (
              <Card key={n} className="animate-pulse">
                <div className="w-full aspect-[9/16] max-h-40 bg-bg-tertiary rounded-md mb-4" />
                <div className="h-4 bg-bg-tertiary rounded w-3/4 mb-2" />
                <div className="h-3 bg-bg-tertiary rounded w-1/2" />
              </Card>
            ))}
          </div>
        )}

        {/* Template grid */}
        {!loading && !error && templates.length === 0 && (
          <Card className="text-center py-12">
            <Video className="w-10 h-10 text-text-tertiary mx-auto mb-3" />
            <p className="text-text-secondary font-medium">No templates found</p>
            <p className="text-sm text-text-tertiary mt-1">
              Active templates will appear here once they are added to Firestore.
            </p>
          </Card>
        )}

        {!loading && !error && templates.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {templates.map((template) => {
              const hasVideo = !!template.previewVideoUrl && !videoErrors.has(template.id);
              const hasImage = !!template.previewImageUrl;

              return (
                <Card key={template.id} hover padding="none" className="flex flex-col overflow-hidden">
                  {/* Preview */}
                  <div className="w-full aspect-[9/16] bg-bg-tertiary flex items-center justify-center overflow-hidden flex-shrink-0">
                    {hasVideo ? (
                      <video
                        src={template.previewVideoUrl}
                        autoPlay
                        loop
                        muted
                        playsInline
                        aria-label={`${template.name} preview`}
                        onError={() => handleVideoError(template.id)}
                        className="w-full h-full object-cover"
                      />
                    ) : hasImage ? (
                      <img
                        src={template.previewImageUrl}
                        alt={`${template.name} preview`}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="flex flex-col items-center text-text-tertiary p-4">
                        <Video className="w-8 h-8 mb-1" />
                        <span className="text-xs">No preview</span>
                      </div>
                    )}
                  </div>

                  {/* Card body */}
                  <div className="p-4 flex flex-col gap-3 flex-1">
                    {/* Name + version */}
                    <div className="flex items-start justify-between gap-2">
                      <h3 className="text-sm font-semibold text-text-primary leading-tight">
                        {template.name}
                      </h3>
                      <Badge variant="success" size="sm" className="flex-shrink-0">
                        v{template.version}
                      </Badge>
                    </div>

                    {/* Metadata row */}
                    <div className="flex flex-wrap items-center gap-3 text-xs text-text-secondary">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {template.duration}s
                      </span>
                      <span className="flex items-center gap-1">
                        <Layers className="w-3.5 h-3.5" />
                        {template.slots.filter((s) => s.visible !== false).length} slot{template.slots.filter((s) => s.visible !== false).length === 1 ? '' : 's'}
                      </span>
                    </div>

                    {/* Format badge */}
                    <div>
                      <Badge variant="default" size="sm">
                        {FORMAT_LABELS[template.format] ?? template.format}
                      </Badge>
                    </div>

                    {/* CTA */}
                    <div className="mt-auto pt-2">
                      <Button
                        variant="primary"
                        size="sm"
                        className="w-full"
                        onClick={() => navigate(`/generate?templateId=${template.id}`)}
                      >
                        Use template
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

export default TemplatesPage;
